/*
 * Convert to Infographic — DeckorateAPI client.
 *
 * Talks to POST /v1/deckorate/slides/convert-infographic on the Deckorate content
 * backend (DeckorateAPI/routes/v1/infographic/infographic.py). The client
 * sends the extracted source content (a text box's text, a table's cells, or
 * a whole slide's shapes) plus the deck's live theme colours; the backend
 * runs a 2-stage AI pipeline (Infographic Planner -> OOXML Composer) and
 * returns the inner <p:spTree> shape XML for the add-in to wrap into a slide
 * and insert. Field naming mirrors the FastAPI models exactly (snake_case
 * envelope, e.g. `request_id`, `slide_dimensions`, `theme_colors`,
 * `user_description`).
 */

import type { HttpClient, HttpRequestOptions } from "../../../services/deckorateApi";
import type { SlideDimensions, ThemeColors } from "../../create-slide/service/slideGenerationApi";
import { logConnectorFailure } from "../../../../../telemetry";

/** connectorSystemName tag for all Convert to Infographic telemetry. */
const INFOGRAPHIC_CONNECTOR = "deckorate";

/** The DeckorateAPI route this feature drives. */
const CONVERT_INFOGRAPHIC_PATH = "/v1/deckorate/slides/convert-infographic";

/** How long to wait for one convert_infographic call before giving up on it.
 *  Mirrors Fix Alignment's ALIGNMENT_REQUEST_TIMEOUT_MS (90s for one AI layout
 *  pass); a fallback tier here can issue up to two such calls in sequence
 *  (region-sized generate, then full-slide "constrain" regenerate), so each
 *  gets its own deadline rather than sharing one across the whole run. */
export const INFOGRAPHIC_REQUEST_TIMEOUT_MS = 90_000;

/** Matches InfographicContent.text's max_length — trimmed client-side so a
 *  large selection doesn't 422 outright. */
export const MAX_INFOGRAPHIC_TEXT_CHARS = 8000;

/** Matches TransformInfographicRequest.user_description's max_length. */
export const MAX_USER_DESCRIPTION_CHARS = 2000;

export type InfographicContentType = "text" | "table" | "slide";
export type InfographicMode = "full_slide" | "region";

export interface InfographicTableContent {
  headers: string[];
  rows: string[][];
}

export interface InfographicSlideContent {
  title: string | null;
  blocks: string[];
}

/** Discriminated by `type` — exactly one of text/table/slide is meaningful. */
export interface InfographicContent {
  type: InfographicContentType;
  text?: string | null;
  table?: InfographicTableContent | null;
  slide?: InfographicSlideContent | null;
}

interface ConvertInfographicRequest {
  request_id?: string;
  content: InfographicContent;
  slide_dimensions: SlideDimensions;
  mode: InfographicMode;
  theme_colors?: ThemeColors | null;
  user_description?: string | null;
}

interface RawConvertInfographicMeta {
  request_id?: unknown;
  model_id?: unknown;
  mode?: unknown;
  processing_ms?: unknown;
}

/** Deliberately `unknown`-typed: this is wire data, validated before use. */
interface RawConvertInfographicResponse {
  request_id?: unknown;
  infographic_type?: unknown;
  xml?: unknown;
  valid?: unknown;
  attempts?: unknown;
  plan?: unknown;
  error?: unknown;
  meta?: RawConvertInfographicMeta;
}

export interface ConvertInfographicResult {
  readonly requestId: string;
  readonly infographicType: string | null;
  readonly xml: string | null;
  readonly valid: boolean;
  readonly attempts: number;
  readonly error: string | null;
}

/** Reports a Convert to Infographic failure through the shared telemetry
 *  pipeline (PII-scrubbed connectorSystemName-tagged event). */
export function logInfographicError(reason: string, error: unknown): void {
  logConnectorFailure(INFOGRAPHIC_CONNECTOR, error, reason);
}

/** Centralized so every catch site in this feature extracts messages the same way. */
export function getInfographicErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

/** True when a rejection came from an AbortController rather than the server. */
export function isInfographicAbortError(error: unknown): boolean {
  return error instanceof DOMException
    ? error.name === "AbortError"
    : error instanceof Error && error.name === "AbortError";
}

/* ── Error copy ──────────────────────────────────────────────────────────────
 * createHttpClient throws `Deckorate POST /v1/deckorate/slides/convert-infographic:
 * <status> <rawBody>` — describeInfographicFailure turns that into a sentence
 * worth showing in the sheet/chat: the API's own `message` when it explains
 * something actionable, plain-English copy otherwise.
 */
const INFOGRAPHIC_HTTP_ERROR_PREFIX = "Deckorate ";
const INFOGRAPHIC_HTTP_ERROR_SEPARATOR = ": ";
const INFOGRAPHIC_LEADING_STATUS_PATTERN = /^\d{3}/;

interface ParsedInfographicHttpError {
  readonly status: number;
  readonly body: string;
}

function parseInfographicHttpError(raw: string): ParsedInfographicHttpError | null {
  if (!raw.startsWith(INFOGRAPHIC_HTTP_ERROR_PREFIX)) return null;
  const separator = raw.indexOf(INFOGRAPHIC_HTTP_ERROR_SEPARATOR, INFOGRAPHIC_HTTP_ERROR_PREFIX.length);
  if (separator === -1) return null;

  const remainder = raw.slice(separator + INFOGRAPHIC_HTTP_ERROR_SEPARATOR.length);
  const status = INFOGRAPHIC_LEADING_STATUS_PATTERN.exec(remainder);
  if (!status) return null;

  return { status: Number(status[0]), body: remainder.slice(status[0].length).trim() };
}

const INFOGRAPHIC_HTTP_STATUS = {
  unauthorized: 401,
  forbidden: 403,
  tooManyRequests: 429,
  serverError: 500,
} as const;

const INFOGRAPHIC_UNAUTHORIZED_MESSAGE = "Your session has expired — sign in again and retry.";
const INFOGRAPHIC_BUSY_MESSAGE = "The infographic service is busy right now — try again in a moment.";
const INFOGRAPHIC_SERVER_MESSAGE = "The infographic service is unavailable right now. Nothing was changed.";
export const INFOGRAPHIC_TIMEOUT_MESSAGE =
  "The infographic service took too long to respond. Nothing was changed — try again.";
export const INFOGRAPHIC_CANCELLED_MESSAGE = "Creating the infographic was cancelled. Nothing was changed.";
export const EMPTY_CONTENT_MESSAGE =
  "Select a chart, table, or some text to convert — or move to a slide that has content.";

/** Pull `message`/`error.message` out of either error envelope DeckorateAPI can return. */
function infographicApiErrorMessage(rawBody: string): string | null {
  try {
    const parsed: unknown = JSON.parse(rawBody);
    if (typeof parsed !== "object" || parsed === null) return null;
    const body = parsed as { message?: unknown; error?: { message?: unknown } };
    if (typeof body.message === "string" && body.message) return body.message;
    const nested = body.error?.message;
    return typeof nested === "string" && nested ? nested : null;
  } catch {
    return null;
  }
}

export function describeInfographicFailure(
  error: unknown,
  fallback: string,
  options: { timedOut?: boolean } = {},
): string {
  if (isInfographicAbortError(error)) {
    return options.timedOut ? INFOGRAPHIC_TIMEOUT_MESSAGE : INFOGRAPHIC_CANCELLED_MESSAGE;
  }

  const raw = getInfographicErrorMessage(error, fallback);
  const parsed = parseInfographicHttpError(raw);
  if (!parsed) return raw;

  const { status } = parsed;
  const apiMessage = infographicApiErrorMessage(parsed.body);

  if (status === INFOGRAPHIC_HTTP_STATUS.unauthorized || status === INFOGRAPHIC_HTTP_STATUS.forbidden) {
    return INFOGRAPHIC_UNAUTHORIZED_MESSAGE;
  }
  if (status === INFOGRAPHIC_HTTP_STATUS.tooManyRequests) return INFOGRAPHIC_BUSY_MESSAGE;
  if (status >= INFOGRAPHIC_HTTP_STATUS.serverError) return INFOGRAPHIC_SERVER_MESSAGE;
  // 4xx (validation, EMPTY_CONTENT) — the API's message says exactly what.
  return apiMessage ?? raw;
}

/**
 * `request_id` is a non-secret correlation id the backend echoes back for
 * request/response tracing. The API fills one in when absent, so on a host
 * without `crypto.randomUUID` we simply omit it rather than inventing a
 * weaker id (mirrors Fix Alignment's newRequestId()).
 */
function newInfographicRequestId(): string | undefined {
  return globalThis.crypto?.randomUUID?.();
}

function normaliseResult(raw: RawConvertInfographicResponse | null | undefined): ConvertInfographicResult {
  const body = raw ?? {};
  return {
    requestId: typeof body.request_id === "string" ? body.request_id : "",
    infographicType: typeof body.infographic_type === "string" ? body.infographic_type : null,
    xml: typeof body.xml === "string" && body.xml ? body.xml : null,
    valid: body.valid === true,
    attempts: typeof body.attempts === "number" ? body.attempts : 1,
    error: typeof body.error === "string" && body.error ? body.error : null,
  };
}

/** POST the extracted content and return the validated infographic result. */
export async function postConvertInfographic(
  http: HttpClient,
  content: InfographicContent,
  slideDimensions: SlideDimensions,
  options: {
    mode?: InfographicMode;
    themeColors?: ThemeColors | null;
    userDescription?: string | null;
    signal?: AbortSignal;
  } = {},
): Promise<ConvertInfographicResult> {
  const trimmedDescription = options.userDescription?.trim() || undefined;
  const request: ConvertInfographicRequest = {
    request_id: newInfographicRequestId(),
    content,
    slide_dimensions: slideDimensions,
    mode: options.mode ?? "full_slide",
    ...(options.themeColors ? { theme_colors: options.themeColors } : {}),
    ...(trimmedDescription ? { user_description: trimmedDescription.slice(0, MAX_USER_DESCRIPTION_CHARS) } : {}),
  };

  const requestOptions: HttpRequestOptions = options.signal ? { signal: options.signal } : {};
  try {
    const response = await http.post<RawConvertInfographicResponse | null>(
      CONVERT_INFOGRAPHIC_PATH,
      request,
      requestOptions,
    );
    return normaliseResult(response);
  } catch (error) {
    // Cancellations aren't failures — only report genuine request/parse errors.
    if (!isInfographicAbortError(error)) logInfographicError("infographic-convert-request-failed", error);
    throw error;
  }
}
